import { clsx } from 'clsx'
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion'
import { springStiff } from '@/lib/motionConfig'
import { haptic } from '@/lib/haptics'

interface CheckboxProps {
  checked: boolean
  onChange: () => void
  size?: 'sm' | 'md'
  ariaLabel?: string
}

const svgSizes = { sm: 12, md: 16 }

// 완료 시 체크박스에서 방사되는 점들의 각도(라디안)
const BURST_DOTS = [
  { angle: -Math.PI / 2 }, // 위
  { angle: -Math.PI / 6 }, // 우상
  { angle: (-5 * Math.PI) / 6 }, // 좌상
  { angle: Math.PI / 2 }, // 아래
]

export function Checkbox({ checked, onChange, size = 'md', ariaLabel }: CheckboxProps) {
  const shouldReduceMotion = useReducedMotion()

  return (
    <motion.button
      type="button"
      role="checkbox"
      aria-checked={checked}
      aria-label={ariaLabel ?? (checked ? '선택 해제' : '선택')}
      onClick={(e) => {
        e.stopPropagation()
        if (!checked) {
          haptic('light')
        }
        onChange()
      }}
      // whileTap으로 누름 피드백 (reduced-motion 시 스케일 변화 없음)
      whileTap={shouldReduceMotion ? undefined : { scale: 0.9 }}
      // 체크 상태 진입 시 0.8 → 1.15 → 1 스프링 팝
      animate={
        shouldReduceMotion
          ? undefined
          : checked
            ? { scale: [0.8, 1.15, 1] }
            : { scale: 1 }
      }
      transition={springStiff}
      className="checkbox-control"
    >
      <span
        className={clsx(
          'checkbox-icon',
          size === 'sm' && 'checkbox-icon--sm',
          checked && 'checkbox-icon--selected'
        )}
        aria-hidden="true"
      >
        {checked && (
          <svg
            width={svgSizes[size]}
            height={svgSizes[size]}
            viewBox="0 0 24 24"
            fill="none"
            className="checkbox-icon__check"
          >
            <path
              d="M5 13l4 4L19 7"
              stroke="currentColor"
              strokeWidth={3.5}
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{
                strokeDasharray: 24,
                strokeDashoffset: 0,
              }}
              className="animate-check-stroke"
            />
          </svg>
        )}

        {/* 완료 순간 방사형 점 버스트 — reduced-motion 시 렌더 안 함 */}
        {!shouldReduceMotion && (
          <AnimatePresence>
            {checked && (
              <span
                key="burst"
                className="pointer-events-none absolute inset-0 flex items-center justify-center"
              >
                {BURST_DOTS.map((dot, i) => (
                  <motion.span
                    key={i}
                    className="absolute w-1 h-1 rounded-full"
                    style={{ backgroundColor: 'var(--color-success-500)' }}
                    initial={{ opacity: 0.9, scale: 1, x: 0, y: 0 }}
                    animate={{
                      opacity: 0,
                      scale: 0.4,
                      x: Math.cos(dot.angle) * 14,
                      y: Math.sin(dot.angle) * 14,
                    }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.4, ease: 'easeOut' }}
                  />
                ))}
              </span>
            )}
          </AnimatePresence>
        )}
      </span>
    </motion.button>
  )
}
